import {
  AUDIENCE_EXPORT_TYPES,
  ENUM_REGEX,
  ENUM_SYSTEM_USED,
  EXPORT_MODELED_AUDIENCES,
  EXPORT_TYPES_FIELDS_REQUIRED,
  isKdsNonProductionBuild,
  LoadingStates,
  META_MODEL_CUT_SIZE_MULTIPLIER,
  ValidationErrorMessages,
} from '../../constants';
import {
  generatCustomExportOptions,
  generateOtsExportOptions,
  handleDecimalBeforeInput,
  handleDecimalInput,
  handleDecimalKeyDown,
  isAdvertiserDirectValid,
  isCurrencyRateValid,
  isDescriptionValid,
  isNexxenDatesValid,
  isTTDIdValid,
  isTvCpmValid,
  isViantIdValid,
  validateNexxenAdvertiserDestinationId,
  validateNexxenMarketDestinationId,
} from '../../helper/helper';
import { useAppSelector } from 'redux/hooks';

import AmazonIcon from '../../assets/images/amazon_icon.png';
import AppsflyerIcon from '../../assets/images/appsflyer_icon.png';
import ExperianIcon from '../../assets/images/experian_icon.png';
import LiveRampIcon from '../../assets/images/liveramp_icon.ico';
import MagniteIcon from '../../assets/images/magnite_icon.png';
import MetaIcon from '../../assets/images/meta_icon.png';
import NexxenIcon from '../../assets/images/nexxen_icon.png';
import RedditIcon from '../../assets/images/reddit_icon.png';
import TiktokIcon from '../../assets/images/tiktok_icon.png';
import ViantIcon from '../../assets/images/viant_icon.svg';
import XIcon from '../../assets/images/x_icon.png';
import YahooIcon from '../../assets/images/yahoo_icon.png';

// Material UI Components
import DynamicForm from 'components/Form';
import tokens from 'tokens';
import {
  ADD_AUDIENCE_DETAILS,
  AUDIENCE_EXPORT_FIELD_TITLES,
  ESTIMATED_AUDIENCE_SIZE,
  LIVERAMP_SEGMENT_FIELD_NAME,
  NEXXEN_AVAILABILITY_SINGLE,
  NEXXEN_NONPROD_DEFAULT_ADVERTISER_LOCKED_HELPER,
  NEXXEN_NONPROD_DEFAULT_MARKET_LOCKED_HELPER,
  NEXXEN_RATE_TYPE_FF,
  TRADE_DESK_SEGMENT_FIELD_NAME,
} from '../../content';
import { Grid, TextField } from '@attain-sre/attain-design-system';
import { LoadingAudience } from 'pages/audienceManager/editor';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { useEffect, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLiveRampIntigrations } from '../../graphql/hooks/useLiveRampExportData';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    informationText: {
      marginTop: '1rem',
      fontFamily: 'Calibre-Regular,sans-serif',
      fontSize: 16,
    },
    segmentDivider: {
      background: tokens.colorDividerLgBorder,
      height: '1px',
      border: 'none',
    },
    segmentInputFieldsContainer: {
      display: 'flex',
      padding: '1rem 1rem 0rem 1rem',
    },
    segmentInputFieldsLeft: {
      flex: 1,
      borderRight: '1px solid' + tokens.colorDividerLgBorder,
      paddingRight: '1rem',
    },
    segmentInputFieldsRight: {
      flex: 1,
      paddingLeft: '1rem',
    },
    tradeDeskDataSegmentLabel: {
      paddingBottom: '1.5rem',
      fontSize: 20,
      fontWeight: 500,
      fontFamily: 'Calibre-Regular,sans-serif',
    },
    divider: {
      border: '1px solid #e4e4e4',
      marginTop: 20,
      width: '100%',
    },
    exportContainer: {
      backgroundColor: 'rgb(240, 239, 241)',
      padding: 15,
      marginTop: 18,
      borderRadius: 8,
      width: '100%',
    },
    subtitles: {
      paddingTop: 5,
      paddingBottom: '1.5rem',
      fontSize: 18,
      fontWeight: 500,
      fontFamily: 'Calibre-Bold,sans-serif',
    },
    OrLabel: {
      fontSize: 18,
      fontWeight: 500,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Calibre-Regular,sans-serif',
    },
    destinations: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 10,
    },
    destinationChip: {
      fontSize: 16,
      height: 28,
      border: '1px solid black',
      backgroundColor: '#ffffff',
      color: 'black',
      fontFamily: 'Calibre-Regular,sans-serif',
      padding: '15px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '16px',
      cursor: 'pointer',
      gap: '5px',
      borderColor: 'rgb(89 89 89)',
    },
    destinationSelectedChip: {
      fontSize: 16,
      height: 28,
      border: '1px solid black',
      backgroundColor: 'rgb(225, 244, 234)',
      color: 'black',
      fontFamily: 'Calibre-Regular,sans-serif',
      padding: '15px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '16px',
      cursor: 'pointer',
      gap: '5px',
      borderColor: 'rgb(89 89 89)',
    },
    destinationLabel: {
      fontFamily: 'Calibre-Regular,sans-serif',
      fontSize: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    audienceFieldTitle: {
      fontSize: 18,
      fontWeight: 500,
      fontFamily: 'Calibre-Regular,sans-serif',
    },
  })
);

const InAudienceExport = (props: any) => {
  const classes = useStyles();
  const {
    uiAudienceManagerExportLiveRampVisible = false,
    uiAudienceManagerInhouseExportLiveRampOtsVisible = false,
    uiAudienceManagerInhouseExportExperianOtsVisible = false,
    uiAudienceManagerInhouseOtsExportDemoExercise = false,
    uiAudienceManagerInhouseExportModeledOtsAudienceVisible = false,
    uiAudienceManagerExportYahooDspVisible = false,
    uiAudienceManagerExportTiktokVisible = false,
    uiAudienceManagerModeledCustomExportRedditVisible = false,
    uiAudienceManagerModeledCustomExportAmazonVisible = false,
    uiAudienceManagerModeledCustomExportAppsflyerVisible = false,
    uiAudienceManagerModeledCustomExportMetaVisible = false,
    uiAudienceManagerModeledOtsExportNexxenVisible = false,
    uiAudienceManagerModeledCustomExportNexxenVisible = false,
    uiAudienceManagerModeledCustomExportViantVisible = false,
  } = useFlags();

  const {
    selectedExportType = [],
    handleSelectedExportType,
    selectedSeedAudienceExportTypes = [],
    segmentName,
    description,
    cpm,
    percentageOfMedia,
    isSegmentNameValid,
    inValidSegmentNameErrorMessage,
    isCPMValid,
    cpmErrorMessage,
    isPercentageOfMediaValid,
    percentageOfMediaErrorMessage,
    formData,
    onChange,
    exportDynamicFields = [],
    isSelectAudienceVisible,
    audienceType,
  } = props;
  const isCombinedAudience =
    audienceType === ENUM_SYSTEM_USED.COMBINED_AUDIENCE;
  const otsDefaultOptions = generateOtsExportOptions(audienceType);
  const customDefaultOptions = generatCustomExportOptions(audienceType);

  const [otsOptions, setOtsOptions] = useState(otsDefaultOptions);
  const [customOptions, setCustomOptions] = useState(customDefaultOptions);

  const audienceLoading = useAppSelector(
    state => state.audienceReducer.ui.audienceLoading
  );

  const {
    liveRampIntegrations = [],
    liveRampDistributionManagers = [],
    refetch: refetchLiveRampIntegrations,
  } = useLiveRampIntigrations({
    distributionManagerId: '',
    distributionManagerName: '',
    integrationId: '',
    integrationName: '',
  });

  useEffect(() => {
    refetchLiveRampIntegrations();
  }, []);

  useEffect(() => {
    if (audienceType !== ENUM_SYSTEM_USED.COMBINED_AUDIENCE) {
      updateLaunchDarklyFlags();
    }
  }, [
    uiAudienceManagerExportLiveRampVisible,
    uiAudienceManagerInhouseExportLiveRampOtsVisible,
    uiAudienceManagerInhouseExportExperianOtsVisible,
    uiAudienceManagerInhouseOtsExportDemoExercise,
    uiAudienceManagerExportYahooDspVisible,
    uiAudienceManagerExportTiktokVisible,
    uiAudienceManagerModeledCustomExportRedditVisible,
    uiAudienceManagerModeledCustomExportAmazonVisible,
    uiAudienceManagerModeledCustomExportAppsflyerVisible,
    uiAudienceManagerModeledCustomExportMetaVisible,
    uiAudienceManagerModeledOtsExportNexxenVisible,
    uiAudienceManagerModeledCustomExportNexxenVisible,
    uiAudienceManagerModeledCustomExportViantVisible,
  ]);

  /**
   * Update LiveRamp options dynamically based on flag visibility
   */
  const updateLaunchDarklyFlags = () => {
    // Only add LiveRamp option if the flag is true
    if (
      uiAudienceManagerExportLiveRampVisible &&
      !customOptions.some(
        (x) => x.id === AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT,
        label: 'LiveRamp',
        icon: LiveRampIcon,
      });
    }

    // Only add LiveRamp option if the flag is true
    if (
      uiAudienceManagerInhouseExportLiveRampOtsVisible &&
      !otsOptions.some(
        (x) => x.id === AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT
      )
    ) {
      otsOptions.push({
        id: AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT,
        label: 'LiveRamp',
        icon: LiveRampIcon,
      });
    }

    // Only add LiveRamp option if the flag is true
    if (
      uiAudienceManagerInhouseExportExperianOtsVisible &&
      !otsOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT
      )
    ) {
      otsOptions.push({
        id: AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT,
        label: 'Experian',
        icon: ExperianIcon,
      });
    }

    if (
      uiAudienceManagerInhouseOtsExportDemoExercise &&
      !otsOptions.some(x => x.id === AUDIENCE_EXPORT_TYPES.X_OTS_SEGMENT)
    ) {
      otsOptions.push({
        id: AUDIENCE_EXPORT_TYPES.X_OTS_SEGMENT,
        label: 'X',
        icon: XIcon,
      });
    }

    if (
      uiAudienceManagerInhouseOtsExportDemoExercise &&
      !otsOptions.some(x => x.id === AUDIENCE_EXPORT_TYPES.META_OTS_SEGMENT)
    ) {
      otsOptions.push({
        id: AUDIENCE_EXPORT_TYPES.META_OTS_SEGMENT,
        label: 'Meta',
        icon: MetaIcon,
      });
    }

    if (
      uiAudienceManagerInhouseOtsExportDemoExercise &&
      !otsOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.MAGNITE_OTS_SEGMENT
      )
    ) {
      otsOptions.push({
        id: AUDIENCE_EXPORT_TYPES.MAGNITE_OTS_SEGMENT,
        label: 'Magnite',
        icon: MagniteIcon,
      });
    }

    if (
      uiAudienceManagerExportYahooDspVisible &&
      !isCombinedAudience &&
      !otsOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT
      )
    ) {
      otsOptions.push({
        id: AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT,
        label: 'Yahoo DSP',
        icon: YahooIcon,
      });
    }

    if (
      uiAudienceManagerInhouseOtsExportDemoExercise &&
      !customOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.X_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.X_CUSTOM_SEGMENT,
        label: 'X',
        icon: XIcon,
      });
    }

    if (
      uiAudienceManagerInhouseOtsExportDemoExercise &&
      !customOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT,
        label: 'Meta',
        icon: MetaIcon,
      });
    }

    if (
      uiAudienceManagerInhouseOtsExportDemoExercise &&
      !customOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.MAGNITE_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.MAGNITE_CUSTOM_SEGMENT,
        label: 'Magnite',
        icon: MagniteIcon,
      });
    }

    if (
      uiAudienceManagerExportYahooDspVisible &&
      !isCombinedAudience &&
      !customOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT,
        label: 'Yahoo DSP',
        icon: YahooIcon,
      });
    }

    if (
      uiAudienceManagerExportTiktokVisible &&
      !isCombinedAudience &&
      !customOptions.some(
        (x) => x.id === AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT,
        label: 'TikTok',
        icon: TiktokIcon,
      });
    }

    if (
      uiAudienceManagerModeledCustomExportRedditVisible &&
      !isCombinedAudience &&
      !customOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.REDDIT_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.REDDIT_CUSTOM_SEGMENT,
        label: 'Reddit',
        icon: RedditIcon,
      });
    }

    if (
      uiAudienceManagerModeledCustomExportAmazonVisible &&
      !isCombinedAudience &&
      !customOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT,
        label: 'Amazon',
        icon: AmazonIcon,
      });
    }

    if (
      uiAudienceManagerModeledCustomExportAppsflyerVisible &&
      !isCombinedAudience &&
      !customOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.APPSFLYER_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.APPSFLYER_CUSTOM_SEGMENT,
        label: 'AppsFlyer',
        icon: AppsflyerIcon,
      });
    }

    if (
      uiAudienceManagerModeledCustomExportMetaVisible &&
      !isCombinedAudience &&
      !customOptions.some(
        x => x.id === AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT,
        label: 'Meta',
        icon: MetaIcon,
      });
    }

    if (
      uiAudienceManagerModeledOtsExportNexxenVisible &&
      !isCombinedAudience &&
      !otsOptions.some((x) => x.id === AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT)
    ) {
      otsOptions.push({
        id: AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT,
        label: 'Nexxen',
        icon: NexxenIcon,
      });
    }

    if (
      uiAudienceManagerModeledCustomExportNexxenVisible &&
      !isCombinedAudience &&
      !customOptions.some(
        (x) => x.id === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT,
        label: 'Nexxen',
        icon: NexxenIcon,
      });
    }

    if (
      uiAudienceManagerModeledCustomExportViantVisible &&
      !isCombinedAudience &&
      !customOptions.some(
        (x) => x.id === AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT
      )
    ) {
      customOptions.push({
        id: AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT,
        label: 'Viant',
        icon: ViantIcon,
      });
    }

    setOtsOptions(otsOptions);
    setCustomOptions(customOptions);
  };

  /**
   * Handle click on destination chip (either select or deselect)
   */
  const toggleDestinationChip = (chipId: string) => {
    const otsOptionNames = otsOptions
      .map(x => x.id)
      .filter(x => x !== 'all');

    // Check if the "Toggle All" option is selected
    if (chipId === 'all') {
      // Determine if all OTS options are currently selected
      const selectedOTS =
        selectedExportType?.filter(x => otsOptionNames.includes(x)) || [];
      const isAllOTSSelected = selectedOTS.length === otsOptionNames.length;

      if (isAllOTSSelected) {
        // If all OTS options are selected, deselect all OTS options except 'all'
        const updatedSelection = selectedExportType
          .filter(
            (selectedChip: string) =>
              !otsOptions.some(option => option.id === selectedChip)
          )
          .filter(selectedChip => selectedChip); // Ensure there are no empty values

        // Update the selected export types without the OTS options
        handleSelectedExportType(updatedSelection);
      } else {
        // If not all OTS options are selected, select all OTS options (except 'all' itself)
        // Add all OTS options to the selection (ensure no duplicates)
        handleSelectedExportType([
          ...new Set([...selectedExportType, ...otsOptionNames]),
        ]);
      }
      return;
    }

    // Handle selecting/unselecting specific destination options
    const isDestinationSelected = selectedExportType.find(
      (selectedChip: string) => selectedChip === chipId
    );

    if (isDestinationSelected) {
      // If the chip is already selected, deselect it by filtering it out
      handleSelectedExportType(
        selectedExportType.filter(
          (selectedChip: string) => selectedChip !== chipId
        )
      );
    } else {
      // If the chip is not selected, we need to ensure only one custom option is active at a time
      const customOpt = customOptions.map(x => x.id);
      const isCustomOption = customOpt.includes(chipId);

      let updatedExportTypes = selectedExportType;

      // Only handle custom option deselection if the new selection is also a custom option
      if (isCustomOption) {
        const currentlySelectedCustomExport = selectedExportType.find(
          selectedChip => customOpt.includes(selectedChip)
        );

        // If a custom export type is already selected, remove it before adding the new one
        if (currentlySelectedCustomExport) {
          updatedExportTypes = updatedExportTypes.filter(
            (selectedChip: string) =>
              selectedChip !== currentlySelectedCustomExport
          );
        }
      }

      // Add the new destination chip to the selected export types
      handleSelectedExportType([...updatedExportTypes, chipId]);
    }
  };

  /**
   * Retrive className if a destination chip is selected or not
   */
  const getDestinationChipClass = (id: string) => {
    if (id === 'all') {
      const otsOpt = otsOptions.map(x => x.id).filter(x => x !== 'all');
      const selectedOTS =
        selectedExportType?.filter(x => otsOpt.includes(x)) || [];
      const isAllOTSSelected = selectedOTS.length === otsOpt.length;

      return isAllOTSSelected
        ? classes.destinationSelectedChip
        : classes.destinationChip;
    }
    return selectedExportType.includes(id)
      ? classes.destinationSelectedChip
      : classes.destinationChip;
  };

  /**
   * Map formData value with the corresponding json eliment
   */
  const mapSegmentFormData = (sagmentId, formJson, exportType) => {
    const mappedFormJson = formJson;
    const mappedFormData = formData?.find(x => x.id === sagmentId);
    for (const formField of mappedFormJson) {
      if (formField.column) {
        // Use value from mappedFormData if available, otherwise fall back to default value from config
        formField.value =
          mappedFormData?.[formField.column] ?? formField.value ?? '';
      }

      // Handle label fields with dynamic calculation
      if (formField.type === 'label' && formField.id === 'metaLalRatioLabel') {
        const metaLalRatioValue = mappedFormData?.metaLalRatio;
        if (metaLalRatioValue) {
          const metaModelCutSize = (
            metaLalRatioValue * META_MODEL_CUT_SIZE_MULTIPLIER
          ).toFixed(1);
          formField.htmlContent = `<b>${ESTIMATED_AUDIENCE_SIZE}:</b> About ${metaModelCutSize}M IDs`;
        } else {
          formField.htmlContent = '';
        }
      }

      switch (formField.column) {
        case 'partnerID': {
          formField.disabled = mappedFormData.advertiserID ? true : false;
          formField.required = mappedFormData.advertiserID ? false : true;
          // TTD validation for partnerID - only show error if field has a value
          const isTTDCustomSegment =
            exportType === AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT ||
            exportType ===
              AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE;
          if (
            isTTDCustomSegment &&
            mappedFormData.partnerID !== undefined &&
            mappedFormData.partnerID !== null &&
            mappedFormData.partnerID !== ''
          ) {
            const partnerIdValidation = isTTDIdValid(mappedFormData.partnerID);
            if (!partnerIdValidation.isValid) {
              formField.helperText =
                partnerIdValidation.ttdIdErrorMessage || '';
              formField.error = true;
            } else {
              formField.helperText = '';
              formField.error = false;
            }
          } else {
            // Field is empty/cleared - remove validation error
            formField.helperText = '';
            formField.error = false;
          }
          break;
        }
        case 'advertiserID': {
          formField.disabled = mappedFormData.partnerID ? true : false;
          formField.required = mappedFormData.partnerID ? false : true;
          // TTD validation for advertiserID - only show error if field has a value
          const isTTDCustomSegment =
            exportType === AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT ||
            exportType ===
              AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE;
          const isViantCustomSegment =
            exportType === AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT;
          const hasAdvertiserIdValue =
            mappedFormData.advertiserID !== undefined &&
            mappedFormData.advertiserID !== null &&
            mappedFormData.advertiserID !== '';
          if (isTTDCustomSegment && hasAdvertiserIdValue) {
            const advertiserIdValidation = isTTDIdValid(
              mappedFormData.advertiserID
            );
            if (!advertiserIdValidation.isValid) {
              formField.helperText =
                advertiserIdValidation.ttdIdErrorMessage || '';
              formField.error = true;
            } else {
              formField.helperText = '';
              formField.error = false;
            }
          } else if (isViantCustomSegment && hasAdvertiserIdValue) {
            // Viant validation for advertiserID - must be a positive integer
            const advertiserIdValidation = isViantIdValid(
              mappedFormData.advertiserID,
              ValidationErrorMessages.VIANT_ADVERTISER_ID_POSITIVE_ERROR
            );
            if (!advertiserIdValidation.isValid) {
              formField.helperText =
                advertiserIdValidation.viantIdErrorMessage || '';
              formField.error = true;
            } else {
              formField.helperText = '';
              formField.error = false;
            }
          } else {
            // Field is empty/cleared - remove validation error
            formField.helperText = '';
            formField.error = false;
          }
          break;
        }
        case 'accountID': {
          // Viant validation for accountID - must be a positive integer
          const isViantCustomSegment =
            exportType === AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT;
          if (
            isViantCustomSegment &&
            mappedFormData.accountID !== undefined &&
            mappedFormData.accountID !== null &&
            mappedFormData.accountID !== ''
          ) {
            const accountIdValidation = isViantIdValid(
              mappedFormData.accountID,
              ValidationErrorMessages.VIANT_ACCOUNT_ID_POSITIVE_ERROR
            );
            if (!accountIdValidation.isValid) {
              formField.helperText =
                accountIdValidation.viantIdErrorMessage || '';
              formField.error = true;
            } else {
              formField.helperText = '';
              formField.error = false;
            }
          } else {
            // Field is empty/cleared - remove validation error
            formField.helperText = '';
            formField.error = false;
          }
          break;
        }
        case 'advertiserDestination': {
          // Nexxen: Show advertiserDestination only when availability is SINGLE
          if (exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT) {
            const shouldBeVisible =
              mappedFormData?.availability === NEXXEN_AVAILABILITY_SINGLE;
            formField.isVisible = shouldBeVisible;
            formField.required = shouldBeVisible;
            if (shouldBeVisible) {
              if (isKdsNonProductionBuild()) {
                formField.disabled = true;
                formField.helperText =
                  NEXXEN_NONPROD_DEFAULT_ADVERTISER_LOCKED_HELPER;
                formField.error = false;
              } else {
                formField.disabled = false;
                const advValidation = validateNexxenAdvertiserDestinationId(
                  mappedFormData?.advertiserDestination ?? ''
                );
                if (!advValidation.isValid) {
                  formField.helperText = advValidation.message;
                  formField.error = true;
                } else {
                  formField.helperText = '';
                  formField.error = false;
                }
              }
            } else {
              formField.helperText = '';
              formField.error = false;
            }
          } else {
            if (mappedFormData.platformDestination) {
              const distributors = liveRampDistributionManagers
                .filter(
                  (manager: any) =>
                    manager.integrationId ===
                    mappedFormData.platformDestination.id
                )
                .map((x) => {
                  return {
                    label: x.distributionManagerName,
                    id: x.distributionManagerId,
                  };
                });

              formField.options = distributors;
              formField.disabled = false;
              formField.required = true;
            } else {
              formField.options = liveRampDistributionManagers;
              formField.disabled = true;
              formField.required = false;
            }
          }
          break;
        }
        case 'platformDestination': {
          // Nexxen: Show platformDestination only when availability is SINGLE
          if (exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT) {
            const shouldBeVisible =
              mappedFormData?.availability === NEXXEN_AVAILABILITY_SINGLE;
            formField.isVisible = shouldBeVisible;
            formField.required = shouldBeVisible;
            if (shouldBeVisible) {
              if (isKdsNonProductionBuild()) {
                formField.disabled = true;
                formField.helperText =
                  NEXXEN_NONPROD_DEFAULT_MARKET_LOCKED_HELPER;
                formField.error = false;
              } else {
                formField.disabled = false;
                const marketValidation = validateNexxenMarketDestinationId(
                  mappedFormData?.platformDestination ?? ''
                );
                if (!marketValidation.isValid) {
                  formField.helperText = marketValidation.message;
                  formField.error = true;
                } else {
                  formField.helperText = '';
                  formField.error = false;
                }
              }
            } else {
              formField.helperText = '';
              formField.error = false;
            }
          } else {
            // LiveRamp: Set options for platformDestination
            formField.options = liveRampIntegrations;
          }
          break;
        }
        case 'tvCpm': {
          if (
            [
              AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT,
              AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT,
            ].includes(exportType)
          ) {
            const isYahooDspTvCpmValid = isTvCpmValid(
              exportType,
              mappedFormData.tvCpm
            );
            if (!isYahooDspTvCpmValid.isValid) {
              formField.helperText = isYahooDspTvCpmValid.tvCpmErrorMessage;
              formField.error = true;
            } else {
              formField.helperText = '';
              formField.error = false;
            }
          }
          break;
        }
        case 'advertiserDirect': {
          // LiveRamp validation for advertiserDirect
          const isLiveRampType =
            exportType === AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT ||
            exportType === AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT ||
            exportType ===
              AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE ||
            exportType ===
              AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE;
          if (isLiveRampType) {
            const isAdvertiserDirectValidResult = isAdvertiserDirectValid(
              [exportType],
              mappedFormData.advertiserDirect || ''
            );
            if (!isAdvertiserDirectValidResult.isValid) {
              formField.helperText =
                isAdvertiserDirectValidResult.advertiserDirectErrorMessage ||
                '';
              formField.error = true;
            } else {
              formField.helperText = '';
              formField.error = false;
            }
          }
          break;
        }
        case 'currencyRate': {
          // Nexxen: Show currencyRate only when rateType is NOT Flat Fee
          if (
            exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT ||
            exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT
          ) {
            const shouldBeVisible =
              mappedFormData?.rateType !== NEXXEN_RATE_TYPE_FF;
            formField.isVisible = shouldBeVisible;
            formField.required = shouldBeVisible;

            // Validate currencyRate is greater than 0
            const currencyRateValidation = isCurrencyRateValid(
              mappedFormData?.currencyRate
            );
            if (!currencyRateValidation.isValid) {
              formField.helperText = currencyRateValidation.validationMessage;
              formField.error = true;
            } else {
              formField.helperText = '';
              formField.error = false;
            }
          }
          break;
        }
        case 'endDate': {
          if (
            exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT ||
            exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT
          ) {
            const dateValidation = isNexxenDatesValid(
              mappedFormData?.startDate || '',
              mappedFormData?.endDate || ''
            );
            // Only show error on end date if the issue is specifically with end date
            if (
              !dateValidation.isValid &&
              mappedFormData?.endDate &&
              mappedFormData?.startDate &&
              (dateValidation.validationMessage?.includes('End Date') ||
                dateValidation.validationMessage?.includes('end date'))
            ) {
              formField.helperText = dateValidation.validationMessage || '';
              formField.error = true;
            } else {
              formField.helperText = '';
              formField.error = false;
            }
          }
          break;
        }
        default:
          break;
      }
    }

    return JSON.parse(JSON.stringify(mappedFormJson));
  };

  /**
   * Check control's visibility based on exprort_type
   */
  const { isCPM, isDesciption, isPercentageOfMedia } = useMemo(() => {
    const allSelectedTypes = [
      ...selectedExportType,
      ...selectedSeedAudienceExportTypes,
    ];

    if (allSelectedTypes.length === 0) {
      return { isCPM: true, isPercentageOfMedia: true, isDesciption: true };
    }

    const cpmValidFor = EXPORT_TYPES_FIELDS_REQUIRED.cpm;
    const percentageOfMediaValidFor =
      EXPORT_TYPES_FIELDS_REQUIRED.percentageOfMedia;
    const desciptionValidFor = EXPORT_TYPES_FIELDS_REQUIRED.description;
    const defaultExportTypes = EXPORT_TYPES_FIELDS_REQUIRED.default;

    return {
      isCPM: [...cpmValidFor, ...defaultExportTypes].some((x) =>
        allSelectedTypes.includes(x)
      ),
      isPercentageOfMedia: [...percentageOfMediaValidFor].some((x) =>
        allSelectedTypes.includes(x)
      ),
      isDesciption: [...desciptionValidFor, ...defaultExportTypes].some((x) =>
        allSelectedTypes.includes(x)
      ),
    };
  }, [selectedExportType, selectedSeedAudienceExportTypes]);

  const {
    isValid: isDesciptionValid,
    isRequired: isDesciptionRequied,
    validationMessage: descriptionErrorMessage,
  } = isDescriptionValid(
    [...selectedExportType, ...selectedSeedAudienceExportTypes],
    description
  );

  const { perOfMediaLabel } = useMemo(() => {
    if (
      (selectedExportType.includes(
        AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT
      ) ||
        selectedSeedAudienceExportTypes.includes(
          AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
        ) ||
        selectedExportType.includes(
          AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT
        ) ||
        selectedSeedAudienceExportTypes.includes(
          AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE
        )) &&
      (selectedExportType.includes(AUDIENCE_EXPORT_TYPES.OTS_SEGMENT) ||
        selectedExportType.includes(AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT) ||
        selectedSeedAudienceExportTypes.includes(
          AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE
        ) ||
        selectedSeedAudienceExportTypes.includes(
          AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE
        ))
    ) {
      return {
        perOfMediaLabel: `${TRADE_DESK_SEGMENT_FIELD_NAME.PERCENT_OF_MEDIA} / ${LIVERAMP_SEGMENT_FIELD_NAME.PROGRAMMATIC_PERCENT_OF_MEDIA} *`,
      };
    }
    if (
      selectedExportType.includes(
        AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT
      ) ||
      selectedSeedAudienceExportTypes.includes(
        AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
      ) ||
      selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT) ||
      selectedSeedAudienceExportTypes.includes(
        AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE
      )
    ) {
      return {
        perOfMediaLabel:
          LIVERAMP_SEGMENT_FIELD_NAME.PROGRAMMATIC_PERCENT_OF_MEDIA,
      };
    }

    return {
      perOfMediaLabel: TRADE_DESK_SEGMENT_FIELD_NAME.PERCENT_OF_MEDIA,
    };
  }, [selectedExportType, selectedSeedAudienceExportTypes]);

  const isAudienceLoading = audienceLoading?.state === LoadingStates.LOADING;
  if (isAudienceLoading) return <LoadingAudience />;

  return (
    <>
      <Grid container spacing={0} display="flex">
        <Grid item xs={12} className={classes.tradeDeskDataSegmentLabel}>
          {ADD_AUDIENCE_DETAILS}
        </Grid>
        <Grid
          container
          xs={6}
          display="flex"
          flexDirection="column"
          className={classes.segmentInputFieldsLeft}
        >
          <Grid container spacing={0}>
            <Grid item xs={12}>
              <TextField
                onChange={(e) => onChange('segmentName', e.target.value)}
                value={segmentName}
                fullWidth
                id="segmentName"
                label="Segment Name"
                type="text"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                error={!isSegmentNameValid}
                helperText={inValidSegmentNameErrorMessage}
                required
              />
            </Grid>
          </Grid>

          <Grid container spacing={0} style={{ marginTop: 30 }}>
            {isCPM && (
              <>
                <Grid item xs={5}>
                  <TextField
                    onChange={(e) => onChange('cpm', e.target.value)}
                    value={cpm}
                    fullWidth
                    id="cpm"
                    label={TRADE_DESK_SEGMENT_FIELD_NAME.CPM}
                    type="text"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    required
                    error={!isCPMValid}
                    helperText={cpmErrorMessage}
                    onBeforeInput={(e) => {
                      handleDecimalBeforeInput(e, ENUM_REGEX.DECIMAL);
                    }}
                    onKeyDown={(e) => {
                      handleDecimalKeyDown(e, cpm, ENUM_REGEX.DECIMAL);
                    }}
                    onInput={(e) => {
                      handleDecimalInput(
                        e,
                        cpm,
                        (value) => onChange('cpm', value),
                        ENUM_REGEX.DECIMAL
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={2} />
              </>
            )}
            {isPercentageOfMedia && (
              <Grid item xs={5}>
                <TextField
                  onChange={e => onChange('percentageOfMedia', e.target.value)}
                  value={percentageOfMedia}
                  fullWidth
                  id='percentageOfMedia'
                  label={perOfMediaLabel}
                  type='text'
                  variant='outlined'
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required={
                    !(
                      selectedExportType.includes(
                        AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT
                      ) ||
                      selectedExportType.includes(
                        AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT
                      ) ||
                      selectedSeedAudienceExportTypes.includes(
                        AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE
                      ) ||
                      selectedSeedAudienceExportTypes.includes(
                        AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
                      )
                    )
                  }
                  error={
                    isPercentageOfMediaValid !== undefined
                      ? !isPercentageOfMediaValid
                      : false
                  }
                  helperText={percentageOfMediaErrorMessage || ''}
                  onBeforeInput={(e) => {
                    handleDecimalBeforeInput(e, ENUM_REGEX.DECIMAL);
                  }}
                  onKeyDown={(e) => {
                    handleDecimalKeyDown(
                      e,
                      percentageOfMedia,
                      ENUM_REGEX.DECIMAL
                    );
                  }}
                  onInput={(e) => {
                    handleDecimalInput(
                      e,
                      percentageOfMedia,
                      (value) => onChange('percentageOfMedia', value),
                      ENUM_REGEX.DECIMAL
                    );
                  }}
                />
              </Grid>
            )}
          </Grid>
          {isDesciption && (
            <Grid container spacing={0} style={{ marginTop: 30 }}>
              <Grid item xs={12}>
                <TextField
                  onChange={e => onChange('description', e.target.value)}
                  value={description}
                  fullWidth
                  id='description'
                  label={TRADE_DESK_SEGMENT_FIELD_NAME.DESCRIPTION}
                  type='text'
                  required={isDesciptionRequied}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!isDesciptionValid}
                  helperText={descriptionErrorMessage}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid
          container
          xs={6}
          display='flex'
          flexDirection='column'
          className={classes.segmentInputFieldsRight}
        >
          {exportDynamicFields
            ?.filter(x => x?.formJSON?.length > 0)
            .map((segment, i) => {
              return (
                <>
                  <p
                    className={classes.audienceFieldTitle}
                    style={{
                      borderTop: i == 0 ? '' : '1px solid lightGray',
                      marginTop: i == 0 ? 0 : 22,
                      paddingTop: i == 0 ? 0 : 12,
                    }}
                  >
                    {`Additional ${
                      AUDIENCE_EXPORT_FIELD_TITLES[segment.exportType]
                    } Requirements`}
                  </p>
                  <Grid container spacing={0}>
                    <DynamicForm
                      config={mapSegmentFormData(
                        segment.id,
                        segment.formJSON,
                        segment.exportType
                      )}
                      onChange={(id, value) => onChange(id, value, segment.id)}
                    />
                  </Grid>
                </>
              );
            })}
        </Grid>
        {isSelectAudienceVisible && (
          <>
            <div className={classes.divider} />
            <div className={classes.exportContainer}>
              <Grid item xs={12} className={classes.subtitles}>
                {EXPORT_MODELED_AUDIENCES}
              </Grid>
              <Grid container spacing={0}>
                {uiAudienceManagerInhouseExportModeledOtsAudienceVisible && (
                  <Grid item xs={12} className={classes.destinations}>
                    <div className={classes.destinationLabel}>
                      <b>OTS:</b>
                    </div>
                    {otsOptions.map(x => (
                      <div
                        key={x.id}
                        onClick={() => toggleDestinationChip(x.id)}
                        className={getDestinationChipClass(x.id)}
                      >
                        {x.icon && (
                          <img
                            src={x.icon}
                            alt='ttd-icon'
                            style={{ height: 15, width: 15 }}
                          />
                        )}
                        <span>{x.label}</span>
                      </div>
                    ))}
                  </Grid>
                )}

                <Grid
                  item
                  xs={12}
                  className={classes.destinations}
                  style={{
                    marginTop:
                      uiAudienceManagerInhouseExportModeledOtsAudienceVisible
                        ? 25
                        : 0,
                  }}
                >
                  <div className={classes.destinationLabel}>
                    <b>Custom:</b>
                  </div>
                  {customOptions.map(x => (
                    <div
                      key={x.id}
                      onClick={() => toggleDestinationChip(x.id)}
                      className={getDestinationChipClass(x.id)}
                    >
                      <img
                        src={x.icon}
                        alt='liveramp-icon'
                        style={{ height: 15, width: 15 }}
                        className=''
                      />

                      <span>{x.label}</span>
                    </div>
                  ))}
                </Grid>
              </Grid>
            </div>
          </>
        )}
      </Grid>
    </>
  );
};

export default InAudienceExport;
